// Evidence master compression profiles (docs/fotos-evidencia.md).
//
// The master is compressed ONCE on the device and never cropped: a profile
// only caps the long side (keeping the aspect ratio) and sets the JPEG
// quality. Each quality is the lowest whose SSIMULACRA2 p10 reaches 80 on
// that side (tools/calibracion-fotos, 30 Pixel 7 Pro photos, 2026-09-24).
// The values live in catalogo.json so the app (Dart) reads the very same
// ones; this module validates them and exposes the rules.
import { z } from 'zod';
import datos from '../catalogo.json' with { type: 'json' };

export const perfilFoto = z.enum(['maxima', 'equilibrado', 'ahorro']);
export type PerfilFoto = z.infer<typeof perfilFoto>;

const rango = z.object({ min: z.number().int(), max: z.number().int() });

export const catalogoFotos = z.object({
  perfilPorDefecto: perfilFoto,
  perfiles: z
    .array(
      z.object({
        id: perfilFoto,
        nombre: z.string().min(1),
        ladoMax: z.number().int().nullable(),
        calidad: z.number().int(),
        /** Measured mean master size (KB) on 12 MP photos: cost estimates. */
        kbMedio: z.number().positive(),
      }),
    )
    .length(perfilFoto.options.length),
  calidad: rango,
  ladoMax: rango,
});

const catalogo = catalogoFotos.parse(datos.fotos);

/** Quality limits accepted by actualizarConfig (and the app, defensively). */
export const CALIDAD_FOTO = catalogo.calidad;
/** Long-side limits when capped; null means camera resolution. */
export const LADO_FOTO = catalogo.ladoMax;

export type ParametrosFoto = {
  /** Long side cap in px; null = camera resolution (no resize). */
  ladoMax: number | null;
  /** JPEG quality. */
  calidad: number;
};

export const PERFILES_FOTO = Object.fromEntries(
  catalogo.perfiles.map((p) => [p.id, { ladoMax: p.ladoMax, calidad: p.calidad }]),
) as Readonly<Record<PerfilFoto, Readonly<ParametrosFoto>>>;

/** Measured mean master size per profile, in KB (docs/fotos-evidencia.md §4.2). */
export const KB_MEDIO_FOTO = Object.fromEntries(
  catalogo.perfiles.map((p) => [p.id, p.kbMedio]),
) as Readonly<Record<PerfilFoto, number>>;

/** Profile display names (catalog). */
export const NOMBRE_PERFIL_FOTO = Object.fromEntries(
  catalogo.perfiles.map((p) => [p.id, p.nombre]),
) as Readonly<Record<PerfilFoto, string>>;

/** Camera resolution (maxima): the best evidence. */
export const PERFIL_FOTO_DEFECTO: PerfilFoto = catalogo.perfilPorDefecto;

export const configFotos = z
  .object({
    perfil: perfilFoto,
    ladoMax: z.number().int().min(LADO_FOTO.min).max(LADO_FOTO.max).nullable(),
    calidad: z.number().int().min(CALIDAD_FOTO.min).max(CALIDAD_FOTO.max),
    /**
     * Profiles this workshop may pick (a plan limit, e.g. "maxima" as an
     * upgrade). Absent = all. Only the platform superadmin writes it
     * (actualizarConfig); the chosen profile must be one of them.
     */
    perfilesPermitidos: z
      .array(perfilFoto)
      .min(1)
      .refine((x) => new Set(x).size === x.length, 'Calidades repetidas')
      .optional(),
  })
  .refine((f) => !f.perfilesPermitidos || f.perfilesPermitidos.includes(f.perfil), {
    message: 'La calidad elegida no está entre las permitidas',
    path: ['perfil'],
  });
export type ConfigFotos = z.infer<typeof configFotos>;

/** Profiles a workshop may pick: its list, or every profile when absent. */
export const perfilesPermitidos = (f: Pick<ConfigFotos, 'perfilesPermitidos'> | undefined) =>
  f?.perfilesPermitidos ?? perfilFoto.options;

/**
 * Evidence retention: 1 year, then photos, sealed derivatives and PDFs are
 * deleted. One platform-wide period (not a workshop setting), applied by the
 * bucket lifecycle `Delete` rule (firebase/storage-lifecycle.json, age 366)
 * plus a 365-day bucket retention policy. Inspection data and hashes stay.
 */
export const RETENCION_EVIDENCIA_DIAS = 366;

/** The workshop setting for a plain profile (no advanced overrides). */
export const fotosDePerfil = (perfil: PerfilFoto): ConfigFotos => ({
  perfil,
  ...PERFILES_FOTO[perfil],
});

/** True when ladoMax/calidad differ from the profile's (superadmin only). */
export function fotosAvanzadas(f: ConfigFotos): boolean {
  const p = PERFILES_FOTO[f.perfil];
  return f.ladoMax !== p.ladoMax || f.calidad !== p.calidad;
}

/**
 * Parameters the device must use: the workshop's setting when valid, else
 * the default profile. A malformed config must never degrade the evidence.
 */
export function parametrosFoto(fotos: unknown): ParametrosFoto {
  const r = configFotos.safeParse(fotos);
  const f = r.success ? r.data : fotosDePerfil(PERFIL_FOTO_DEFECTO);
  return { ladoMax: f.ladoMax, calidad: f.calidad };
}
